/* eslint-disable no-console */
import { ThemeComponent } from '@/theme/ThemeComponent';
import { Component, Prop, Ref } from '@eastsideco/salvo-ts';
import axios from 'axios';
import { cart } from '@/theme/Cart';
import { parseUrl, stringifyUrl } from 'query-string';
import { formatMoney } from '@/utils/formatting';

@Component
export class CartMain extends ThemeComponent {
    @Prop({ default: 'cart-main' }) sectionId!: string;

    @Ref() updatingCart!: HTMLElement;
    @Ref({ required: false}) checkoutButton!: HTMLElement;

    // Store error messages temporarily in memory
    private errorMessage: string = '';

    init() {
        // Reload cart state on page load/visibility change to ensure it's in sync
        // This is especially important when returning from checkout
        let isReloading = false;
        const reloadCartOnVisibilityChange = async() => {
            if (isReloading) {
                return;
            }
            isReloading = true;
            try {
                await cart.reload();
                // Trigger a cartChanged event to update the markup if needed
                document.dispatchEvent(new Event('cartChanged'));
            } finally {
                isReloading = false;
            }
        };

        // Reload cart on initial page load to ensure it's in sync
        // This handles cases where the page was loaded with stale cart state
        void reloadCartOnVisibilityChange();

        // Reload cart when page is shown (e.g., returning from checkout via browser back button)
        window.addEventListener('pageshow', (event) => {
            // Only reload if page was loaded from cache (back/forward navigation)
            if (event.persisted) {
                void reloadCartOnVisibilityChange();
            }
        });

        // Reload cart when page becomes visible (e.g., switching tabs back to cart page)
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden && !isReloading) {
                // Only reload if we haven't reloaded recently (debounce)
                setTimeout(() => {
                    if (!document.hidden) {
                        void reloadCartOnVisibilityChange();
                    }
                }, 100);
            }
        });

        // Listen for cart item added
        document.addEventListener('cartChanged', (e: Event) => {
            // Handle item changes
            void this.updateItemsMarkup();

            // Add body class
            document.body.classList.add('cart-has-changed-since-load');

            if (this.checkoutButton) {
                if (cart.isEmpty()) {
                    this.checkoutButton.classList.add('button--disabled');
                } else {
                    this.checkoutButton.classList.remove('button--disabled');
                }
            }

        });
    }

    public setLoading(loading: boolean = false) {
        if (loading) {
            this.updatingCart.classList.add('loading-spinner--active');
        } else {
            this.updatingCart.classList.remove('loading-spinner--active');
        }
    }

    public async updateItemsMarkup() {

        // Store any existing error message before markup update
        const currentErrorMessageElement = this.querySelector('.cart-error');
        if (currentErrorMessageElement) {
            this.errorMessage = currentErrorMessageElement.textContent || '';
        }

        // Small delay to ensure cart API operations have completed and Shopify has processed them
        // Then reload cart state to ensure it's in sync before fetching markup
        await new Promise<void>((resolve) => {
            setTimeout(() => {
                resolve();
            }, 100);
        });
        await cart.reload();

        // Build URL and get latest cart main markup
        const { url, query } = parseUrl(window.location.href, { decode: false });
        const newUrl = stringifyUrl({
            url: url,
            query: {
                ...query,
                sections: `${this.sectionId}`,
            }
        }, { arrayFormat: 'none', encode: false });
        const res = await axios.get(newUrl);

        // Prepare correct markup
        const newCartMain = document.createElement('div');
        newCartMain.innerHTML = res.data[`${this.sectionId}`];
        const newCartMainContainer = newCartMain.querySelector('.cart-main__container');
        const currentCartMainContainer = this.querySelector('.cart-main__container');

        if (newCartMainContainer && currentCartMainContainer) {
            currentCartMainContainer.outerHTML = newCartMainContainer.outerHTML;
        }

        // Update subtotal elements - try multiple approaches to ensure we find them
        // Use a small delay to ensure DOM is fully updated after outerHTML replacement
        setTimeout(() => {
            const insertedContainer = this.querySelector('.cart-main__container');
            if (insertedContainer) {
                this.updateSubtotalElements(insertedContainer);
            } else {
                // Fallback: try without container
                this.updateSubtotalElements();
            }
        }, 10);

        // Restore the error message after updating the cart markup
        const updatedErrorMessageElement = document.querySelector('.cart-error');
        if (updatedErrorMessageElement && this.errorMessage) {
            updatedErrorMessageElement.textContent = this.errorMessage;
            updatedErrorMessageElement.classList.add('show');
        }

        this.setLoading(false);

        // Set cart main loading
        if (this.closest('.side-cart')) {
            this.setLoading(false);
        }

    }

    private updateSubtotalElements(container?: Element) {
        // Find all subtotal elements and update them with current cart total
        const cartTotal = cart.totalPrice;

        if (cartTotal === null || cartTotal === undefined) {
            return;
        }

        // Format the price using the same formatMoney utility used elsewhere
        const formattedPrice = formatMoney(cartTotal);

        // Query from the provided container, or fall back to this or document
        let subtotalElements: NodeListOf<Element>;

        if (container) {
            subtotalElements = container.querySelectorAll('[data-ref="subtotal"]');
        } else {
            // Try querying from this first
            subtotalElements = this.querySelectorAll('[data-ref="subtotal"]');

            // If not found, try document
            if (subtotalElements.length === 0) {
                subtotalElements = document.querySelectorAll('cart-main [data-ref="subtotal"]');
            }
        }

        if (subtotalElements.length > 0) {
            for (const element of subtotalElements) {
                if (element instanceof HTMLElement) {
                    // Use innerHTML to properly render HTML entities like &pound;
                    // This is safe since formattedPrice only contains formatted money values
                    element.innerHTML = formattedPrice;
                }
            }
        }
    }
}
